/**
 * The payroll export. Enterprise (`staff.payroll_export`), and every call
 * needs `staff.payroll.export`.
 *
 * No payroll provider is named anywhere. A layout describes the file the
 * restaurant's own package imports — columns, headers, separator, decimals,
 * how hours and dates are written — and the export follows it.
 *
 * Exporting is two steps on purpose: `useExportPayroll` writes, keeps and
 * audits the file (and may lock the period), then `useDownload` fetches it by
 * `downloadPath(id)`. Fetching the same file again later changes nothing.
 *
 * Hours come back as minutes; show them with `formatMinutes`. Money is a
 * string, and `hourly_rate` null means withheld, not zero.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type { ApiError } from './client';
import { useApi } from './provider';

export interface PayrollField {
  key: string;
  label: string;
  kind: 'text' | 'date' | 'count' | 'hours' | 'money';
}

export interface PayrollColumn {
  field: string;
  header: string;
}

export interface PayrollLayoutInput {
  name: string;
  columns: PayrollColumn[];
  file_format: 'csv' | 'xlsx';
  delimiter: 'comma' | 'semicolon' | 'tab';
  decimal_separator: '.' | ',';
  hours_format: 'decimal' | 'clock' | 'minutes';
  date_format: 'iso' | 'day_month_year' | 'month_day_year' | 'day_month_year_dots';
  include_header: boolean;
  round_to_minutes: number;
}

export interface PayrollLayout extends PayrollLayoutInput {
  id: string;
}

export interface PayrollHoliday {
  day: string;
  name: string;
}

export interface PayrollSiteSettings {
  location_id: string;
  /** Minutes after midnight, site time. Both null: no night hours. */
  night_starts: number | null;
  night_ends: number | null;
  holidays: PayrollHoliday[];
}

export interface PayLine {
  employee_id: string | null;
  employee_code: string;
  employee_name: string;
  shifts: number;
  paid_minutes: number;
  regular_minutes: number;
  overtime_minutes: number;
  /** Counted inside the others too: an uplift, not more hours. */
  night_minutes: number;
  holiday_minutes: number;
  break_minutes: number;
  unpaid_break_minutes: number;
  hourly_rate: string | null;
  tips: string;
}

export interface PayrollPreview {
  location_id: string;
  from_date: string;
  to_date: string;
  currency: string;
  /** Set when the period is locked: the lines are then as they were sent. */
  period_id: string | null;
  locked_at: string | null;
  /** Shifts with no clock-out. The export is refused while there are any. */
  open_entries: number;
  lines: PayLine[];
}

export interface PayrollExport {
  id: string;
  location_id: string;
  from_date: string;
  to_date: string;
  layout_name: string;
  file_format: 'csv' | 'xlsx';
  filename: string;
  people: number;
  created_at: string;
  period_id: string | null;
}

export interface PayrollPeriod {
  id: string;
  location_id: string;
  from_date: string;
  to_date: string;
  locked_at: string;
  unlocked_at: string | null;
  unlock_reason: string | null;
}

export function usePayrollFields(enabled = true) {
  const api = useApi();
  return useQuery({
    queryKey: ['staff', 'payroll', 'fields'],
    queryFn: ({ signal }) => api.get<PayrollField[]>('/v1/staff/payroll/fields', signal),
    enabled,
    staleTime: 3_600_000,
  });
}

/** What a new layout starts with. */
export function usePayrollDefaultColumns(enabled = true) {
  const api = useApi();
  return useQuery({
    queryKey: ['staff', 'payroll', 'default-columns'],
    queryFn: ({ signal }) =>
      api.get<PayrollColumn[]>('/v1/staff/payroll/layouts/default-columns', signal),
    enabled,
    staleTime: 3_600_000,
  });
}

export function usePayrollLayouts(enabled = true) {
  const api = useApi();
  return useQuery({
    queryKey: ['staff', 'payroll', 'layouts'],
    queryFn: ({ signal }) => api.get<PayrollLayout[]>('/v1/staff/payroll/layouts', signal),
    enabled,
    staleTime: 60_000,
  });
}

/** Create a layout (no `id`) or replace one. */
export function useSavePayrollLayout() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<PayrollLayout, ApiError, { id?: string; layout: PayrollLayoutInput }>({
    mutationFn: ({ id, layout }) =>
      id
        ? api.request<PayrollLayout>(`/v1/staff/payroll/layouts/${id}`, {
            method: 'PUT',
            body: layout,
          })
        : api.post<PayrollLayout>('/v1/staff/payroll/layouts', layout),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ['staff', 'payroll', 'layouts'] });
    },
  });
}

export function useRemovePayrollLayout() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<void, ApiError, { id: string }>({
    mutationFn: ({ id }) =>
      api.request<void>(`/v1/staff/payroll/layouts/${id}`, { method: 'DELETE' }),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ['staff', 'payroll', 'layouts'] });
    },
  });
}

export function usePayrollSiteSettings(locationId: string | undefined, enabled = true) {
  const api = useApi();
  return useQuery({
    queryKey: ['staff', 'payroll', 'site-settings', locationId ?? null],
    queryFn: ({ signal }) =>
      api.get<PayrollSiteSettings>(
        `/v1/staff/payroll/site-settings?location_id=${locationId}`,
        signal,
      ),
    enabled: enabled && Boolean(locationId),
  });
}

/** Night hours and holidays, both replaced in full. */
export function useSavePayrollSiteSettings() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<PayrollSiteSettings, ApiError, PayrollSiteSettings>({
    mutationFn: (settings) =>
      api.request<PayrollSiteSettings>('/v1/staff/payroll/site-settings', {
        method: 'PUT',
        body: settings,
      }),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ['staff', 'payroll'] });
    },
  });
}

export function usePayrollPreview(
  locationId: string | undefined,
  fromDate: string,
  toDate: string,
  roundToMinutes = 1,
  enabled = true,
) {
  const api = useApi();
  return useQuery({
    queryKey: [
      'staff',
      'payroll',
      'preview',
      locationId ?? null,
      fromDate,
      toDate,
      roundToMinutes,
    ],
    queryFn: ({ signal }) =>
      api.get<PayrollPreview>(
        `/v1/staff/payroll/preview?location_id=${locationId}&from_date=${fromDate}` +
          `&to_date=${toDate}&round_to_minutes=${roundToMinutes}`,
        signal,
      ),
    enabled: enabled && Boolean(locationId) && fromDate <= toDate,
    retry: false,
  });
}

/** Write, keep and audit the period's file; lock the period if asked. */
export function useExportPayroll() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<
    PayrollExport,
    ApiError,
    { locationId: string; fromDate: string; toDate: string; layoutId: string; lock: boolean }
  >({
    mutationFn: (input) =>
      api.post<PayrollExport>('/v1/staff/payroll/exports', {
        location_id: input.locationId,
        from_date: input.fromDate,
        to_date: input.toDate,
        layout_id: input.layoutId,
        lock: input.lock,
      }),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ['staff', 'payroll'] });
      void queryClient.invalidateQueries({ queryKey: ['staff', 'tip-pools'] });
    },
  });
}

/** Where a kept file is fetched from, for `useDownload`. */
export function payrollDownloadPath(exportId: string): string {
  return `/v1/staff/payroll/exports/${exportId}/file`;
}

export function usePayrollExports(locationId: string | undefined, enabled = true) {
  const api = useApi();
  return useQuery({
    queryKey: ['staff', 'payroll', 'exports', locationId ?? null],
    queryFn: ({ signal }) =>
      api.get<PayrollExport[]>(`/v1/staff/payroll/exports?location_id=${locationId}`, signal),
    enabled: enabled && Boolean(locationId),
  });
}

export function usePayrollPeriods(locationId: string | undefined, enabled = true) {
  const api = useApi();
  return useQuery({
    queryKey: ['staff', 'payroll', 'periods', locationId ?? null],
    queryFn: ({ signal }) =>
      api.get<PayrollPeriod[]>(`/v1/staff/payroll/periods?location_id=${locationId}`, signal),
    enabled: enabled && Boolean(locationId),
  });
}

/** Open a locked period again. Needs `staff.payroll.unlock` and a reason. */
export function useUnlockPayrollPeriod() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<PayrollPeriod, ApiError, { periodId: string; reason: string }>({
    mutationFn: ({ periodId, reason }) =>
      api.post<PayrollPeriod>(`/v1/staff/payroll/periods/${periodId}/unlock`, { reason }),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ['staff', 'payroll'] });
    },
  });
}
